
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import { 
  Zap, ArrowRight, ChevronLeft, Loader2, Globe, Phone, 
  AlertCircle, CheckCircle2, Rocket, Bot, Target, Wallet, Crown, Flame
} from 'lucide-react';
import { BusinessNiche } from '../../types';

const SetupBusiness: React.FC = () => {
  const navigate = useNavigate();
  const { createBusiness, business, isLoading: storeLoading } = useStore();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    niche: 'RETAIL' as BusinessNiche,
    phone: '',
    delivery: { fee: 0, minOrder: 0 },
    payment: { pix: true, card: true, cash: true },
    ai: { name: 'FLUXIL BOT', tone: 'Professional' }
  });

  // Recuperar nome pendente do login
  useEffect(() => {
    const pendingName = localStorage.getItem('pending_brand_name');
    if (pendingName) {
      setFormData(prev => ({
        ...prev,
        name: pendingName,
        slug: pendingName.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')
      }));
    }
  }, []);

  useEffect(() => {
    if (!storeLoading && business) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [business, storeLoading, navigate]);

  const handleNext = () => {
    if (step === 1 && !formData.name) return;
    if (step === 2 && (!formData.phone || !formData.slug)) return;
    setStep(prev => prev + 1);
  };

  const handleFinish = async () => {
    if (loading) return;
    setLoading(true);
    setErrorMessage(null);
    
    try {
        await createBusiness({
            name: formData.name,
            slug: formData.slug.trim().toLowerCase(),
            niche: formData.niche,
            phone: formData.phone,
            delivery_config: { fee: formData.delivery.fee, minOrder: formData.delivery.minOrder },
            payment_settings: { 
              acceptPix: formData.payment.pix, 
              acceptCard: formData.payment.card,
              acceptCash: formData.payment.cash,
              acceptStripe: false
            },
            ai_config: { 
              agentName: formData.ai.name, 
              tone: formData.ai.tone, 
              instructions: `Assistente virtual do ${formData.name}.` 
            }
        });
        // O StoreContext atualizará o estado e o useEffect fará o redirect
    } catch (error: any) {
        setErrorMessage(error.message || "ERRO NA ENGINE. Tente uma URL diferente.");
        setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-offwhite text-black flex flex-col font-sans selection:bg-brand-rose selection:text-white">
      <header className="bg-brand-yellow border-b-[8px] border-black p-8">
          <div className="container mx-auto flex justify-between items-center px-4">
              <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-black border-[5px] border-black flex items-center justify-center shadow-bold rotate-3">
                    <Zap className="w-6 h-6 text-brand-yellow fill-brand-yellow" />
                  </div>
                  <span className="text-4xl font-black tracking-tighter uppercase italic leading-none">RITUAL <span className="text-brand-rose">FLUXIL.</span></span>
              </div>
              <div className="flex gap-2">
                  {[1, 2, 3, 4].map(s => (
                    <div key={s} className={`h-4 border-[3px] border-black transition-all ${step >= s ? 'w-10 bg-black' : 'w-4 bg-white shadow-bold-sm'}`} />
                  ))}
              </div>
          </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-4xl space-y-12">
          {errorMessage && (
            <div className="p-6 bg-brand-rose text-white border-[6px] border-black shadow-bold flex items-center gap-6 font-black uppercase text-lg animate-vibrate">
              <AlertCircle className="w-8 h-8 shrink-0" />
              {errorMessage}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-12 animate-scale-in">
              <h1 className="text-6xl md:text-8xl font-black tracking-tighter uppercase italic leading-[0.8]">
                ASSINE SEU <br/> <span className="bg-brand-cyan text-black px-4 border-[6px] border-black inline-block transform rotate-1 shadow-bold">DESTINO.</span>
              </h1>

              <div className="space-y-6">
                  <label className="text-xs font-black uppercase tracking-[0.4em] block opacity-40 ml-2">NOME DO IMPÉRIO</label>
                  <input 
                    placeholder="EX: BURGER VAULT"
                    className="w-full bg-white border-[6px] border-black p-10 text-4xl font-black italic outline-none focus:bg-brand-yellow shadow-bold transition-all"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value.toUpperCase(), slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')})}
                  />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {(['RETAIL', 'SERVICES', 'TECH', 'CONSULTING'] as BusinessNiche[]).map(n => (
                    <button 
                        key={n}
                        onClick={() => setFormData({...formData, niche: n})}
                        className={`p-6 border-[6px] border-black transition-all font-black uppercase italic shadow-bold ${formData.niche === n ? 'bg-brand-rose text-white translate-x-[-4px] translate-y-[-4px] shadow-bold-lg' : 'bg-white hover:bg-brand-offwhite'}`}
                    >
                        {n}
                    </button>
                ))}
              </div>

              <button 
                disabled={!formData.name} 
                onClick={handleNext} 
                className="btn-mega bg-black text-white px-12 py-6 text-3xl uppercase italic tracking-tighter flex items-center gap-6 disabled:opacity-20 shadow-bold"
              >
                PRÓXIMO PASSO <ArrowRight className="w-10 h-10" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-12 animate-scale-in">
              <div className="flex justify-between items-end">
                  <h1 className="text-6xl md:text-8xl font-black tracking-tighter uppercase italic leading-[0.8]">
                    CANAL DE <br/><span className="bg-brand-green text-black px-4 border-[6px] border-black inline-block transform -rotate-1 shadow-bold">ATAQUE.</span>
                  </h1>
                  <button onClick={() => setStep(step-1)} className="p-4 bg-white border-[4px] border-black hover:bg-brand-rose hover:text-white transition-all shadow-bold-sm"><ChevronLeft className="w-8 h-8" /></button>
              </div>

              <div className="space-y-10">
                  <div className="bg-white border-[6px] border-black p-8 shadow-bold space-y-6">
                      <label className="text-xs font-black uppercase tracking-[0.4em] opacity-40">DOMÍNIO EXCLUSIVO (URL)</label>
                      <div className="flex items-center gap-4 text-2xl md:text-4xl font-black bg-brand-offwhite p-4 border-[4px] border-black italic">
                          <Globe className="w-8 h-8 text-brand-cyan shrink-0" />
                          <span className="text-black/20">fluxil.app/</span>
                          <input 
                            className="bg-transparent border-none p-0 flex-1 outline-none text-black"
                            value={formData.slug}
                            onChange={e => setFormData({...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')})}
                          />
                      </div>
                  </div>

                  <div className="bg-white border-[6px] border-black p-8 shadow-bold space-y-6">
                      <label className="text-xs font-black uppercase tracking-[0.4em] opacity-40">WHATSAPP BUSINESS</label>
                      <div className="flex items-center gap-4 text-3xl font-black bg-brand-offwhite p-4 border-[4px] border-black italic">
                          <Phone className="w-8 h-8 text-brand-green shrink-0" />
                          <input 
                            placeholder="(11) 99999-9999"
                            className="bg-transparent border-none p-0 flex-1 outline-none text-black"
                            value={formData.phone}
                            onChange={e => setFormData({...formData, phone: e.target.value})}
                          />
                      </div>
                  </div>
              </div>

              <button 
                disabled={!formData.phone || !formData.slug} 
                onClick={handleNext} 
                className="btn-mega bg-black text-white px-12 py-6 text-3xl uppercase italic tracking-tighter flex items-center gap-6 shadow-bold"
              >
                MODULAR FINANÇAS <ArrowRight className="w-10 h-10" />
              </button>
            </div>
          )}

          {step >= 3 && (
            <div className="space-y-12 animate-scale-in text-center">
               <h1 className="text-6xl md:text-8xl font-black tracking-tighter uppercase italic leading-[0.8]">
                QUASE <span className="bg-brand-rose text-white px-4 border-[6px] border-black inline-block shadow-bold">PRONTO.</span>
              </h1>
              <p className="text-2xl font-black uppercase italic text-gray-400">Clique para finalizar e lançar sua engine.</p>
              <button 
                  onClick={handleFinish} 
                  disabled={loading} 
                  className="w-full bg-brand-rose text-white py-12 border-[8px] border-black font-black text-5xl uppercase italic tracking-tighter shadow-bold-xl"
                >
                  {loading ? <Loader2 className="w-16 h-16 animate-spin mx-auto" /> : <div className="flex items-center justify-center gap-8">LANÇAR OPERAÇÃO <Crown className="w-16 h-16" /></div>}
                </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SetupBusiness;
